"use client";

import * as React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import { IconChevronRight } from "@tabler/icons-react";
import { SubNavigationItem, usePageSidebar } from "./page-with-sub-sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";

interface SubNavigationSidebarProps {
  navigation: SubNavigationItem[];
  onClick: () => void;
}

export function SubNavigationSidebar({
  navigation,
  onClick,
}: SubNavigationSidebarProps) {
  const pathname = usePathname();
  const { sidebar } = usePageSidebar();

  const renderItem = (item: SubNavigationItem, level = 0): React.ReactNode => {
    const isActive = pathname === item.href;
    const hasChildren = item.children && item.children.length > 0;
    const isGroup = hasChildren;
    const groupId = item.href || item.title;
    const isCollapsed = sidebar.groupIsCollapsed(groupId);
    const Icon = item.icon;

    if (isGroup) {
      return (
        <Collapsible
          key={item.href || item.title}
          open={!isCollapsed}
          onOpenChange={() => sidebar.toggleCollapsedGroup(groupId)}
        >
          <CollapsibleTrigger
            className={cn(
              "flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-sm transition-colors",
              "text-foreground hover:text-foreground hover:bg-[rgb(232_232_232)] dark:hover:bg-[rgb(43_43_45)]",
              level > 0 && "ml-4",
            )}
          >
            <div className="flex items-center gap-3">
              {Icon && <Icon className="h-4 w-4 shrink-0" />}
              <span>{item.title}</span>
            </div>
            <IconChevronRight
              className={cn(
                "h-4 w-4 shrink-0 transition-transform",
                !isCollapsed && "rotate-90",
              )}
            />
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-1">
            <div className="flex flex-col gap-1">
              {item.children?.map((child) => renderItem(child, level + 1))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      );
    }

    return (
      <Link
        key={item.href}
        href={item.href}
        className={cn(
          "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
          isActive
            ? "bg-white font-semibold shadow-[0_0_0_1px_#e4e5e1,0_1px_4px_#14141426] dark:bg-[rgb(33_33_35)] dark:shadow-[0_0_0_1px_#434241,0_1px_4px_#0000008c]"
            : "text-foreground hover:text-foreground hover:bg-[rgb(232_232_232)] dark:hover:bg-[rgb(43_43_45)]",
          level > 0 && "ml-4",
        )}
        onClick={onClick}
      >
        {Icon && <Icon className="h-4 w-4 shrink-0" />}
        <span className="flex-1">{item.title}</span>
        {item.badge && (
          <span className="rounded-full bg-pink-500 px-2 py-0.5 text-xs font-medium text-white">
            {item.badge}
          </span>
        )}
      </Link>
    );
  };

  return (
    <nav className="flex flex-col gap-1">
      {navigation.map((item) => renderItem(item))}
    </nav>
  );
}
